import { ActionReducerMapBuilder, Draft, PayloadAction } from '@reduxjs/toolkit'
import {
  DataList,
  saveErrorInState,
  dismissError as commonDismissError,
  closeAlert as commonCloseAlert
} from '../common'
import { downloadFulfilled, DownloadAction } from '../page'
import type { SubLayoutState } from './types'
import type {
  fetchSubLayouts,
  createSubLayout,
  modifySubLayout,
  copySubLayout,
  deleteSubLayout,
  fetchLayoutResult,
  modifyLayoutIndex,
  exportLayout
} from './subLayoutAPI'

export const selectIdReducer = (state: Draft<SubLayoutState>, action: PayloadAction<number>) => {
  state.selectedIds = Array.from(new Set(state.selectedIds).add(action.payload))
}

export const unselectIdReducer = (state: Draft<SubLayoutState>, action: PayloadAction<number>) => {
  const set = new Set(state.selectedIds)
  set.delete(action.payload)
  state.selectedIds = Array.from(set)
}

export const openModalReducer = (state: Draft<SubLayoutState>, action: PayloadAction<string | null>) => {
  state.openedModal = action.payload
}

export const closeModalReducer = (state: Draft<SubLayoutState>) => {
  state.openedModal = null
  state.alert = null
}

export const clearLayoutResultReducer = (state: Draft<SubLayoutState>) => {
  delete state.layoutResult
}

export const buildExtraReducers = (
  builder: ActionReducerMapBuilder<SubLayoutState>,
  thunks: {
    fetchSubLayouts: typeof fetchSubLayouts
    createSubLayout: typeof createSubLayout
    modifySubLayout: typeof modifySubLayout
    copySubLayout: typeof copySubLayout
    deleteSubLayout: typeof deleteSubLayout
    fetchLayoutResult: typeof fetchLayoutResult
    modifyLayoutIndex: typeof modifyLayoutIndex
    exportLayout: typeof exportLayout
  }
) =>
  builder
    .addCase(thunks.fetchSubLayouts.pending, DataList.fetchPending)
    .addCase(thunks.fetchSubLayouts.fulfilled, DataList.fetchFulfilled)
    .addCase(thunks.fetchSubLayouts.rejected, saveErrorInState(DataList.fetchRejected))
    .addCase(thunks.createSubLayout.pending, (state) => {
      state.fetching = true
    })
    .addCase(thunks.createSubLayout.fulfilled, (state) => {
      state.fetching = false
    })
    .addCase(thunks.createSubLayout.rejected, (state, action) => {
      state.fetching = false
      const error = action.payload as { message: string }
      state.alert = { show: true, variant: 'danger', alertBody: error.message }
    })
    .addCase(thunks.modifySubLayout.pending, (state) => {
      state.fetching = true
    })
    .addCase(thunks.modifySubLayout.fulfilled, (state) => {
      state.fetching = false
    })
    .addCase(thunks.modifySubLayout.rejected, (state, action) => {
      state.fetching = false
      const error = action.payload as { message: string } | undefined
      if (error !== undefined) {
        state.alert = { show: true, variant: 'danger', alertBody: error.message }
      }
    })
    .addCase(thunks.deleteSubLayout.pending, (state) => {
      state.fetching = true
    })
    .addCase(thunks.deleteSubLayout.fulfilled, (state) => {
      state.fetching = false
      state.selectedIds = []
    })
    .addCase(
      thunks.deleteSubLayout.rejected,
      saveErrorInState((state) => {
        state.fetching = false
      })
    )
    .addCase(thunks.fetchLayoutResult.pending, (state) => {
      state.fetching = true
    })
    .addCase(thunks.fetchLayoutResult.fulfilled, (state, action) => {
      state.layoutResult = action.payload
      state.fetching = false
    })
    .addCase(
      thunks.fetchLayoutResult.rejected,
      saveErrorInState((state) => {
        state.fetching = false
      })
    )
    .addCase(thunks.modifyLayoutIndex.pending, (state) => {
      state.fetching = false
    })
    .addCase(thunks.modifyLayoutIndex.fulfilled, (state, action) => {
      const { layoutResult } = action.payload as { layoutResult: unknown }
      state.layoutResult = layoutResult
      state.fetching = false
    })
    .addCase(
      thunks.modifyLayoutIndex.rejected,
      saveErrorInState((state, action) => {
        state.fetching = false
        const error = action.payload
        if (error !== undefined) {
          state.alert = { show: true, variant: 'danger', alertBody: error.message }
        }
      })
    )
    .addCase(thunks.copySubLayout.pending, (state) => {
      state.fetching = true
    })
    .addCase(thunks.copySubLayout.fulfilled, (state) => {
      state.fetching = false
    })
    .addCase(thunks.copySubLayout.rejected, (state, action) => {
      state.fetching = false
      const error = action.payload as { message: string } | undefined
      if (error !== undefined) {
        state.alert = { show: true, variant: 'danger', alertBody: error.message }
      }
    })
    .addCase(thunks.exportLayout.pending, (state) => {
      state.fetching = true
    })
    .addCase(thunks.exportLayout.fulfilled, (state, action) => {
      downloadFulfilled(state, action as unknown as DownloadAction)
      state.fetching = false
    })
    .addCase(
      thunks.exportLayout.rejected,
      saveErrorInState((state) => {
        state.fetching = false
      })
    )

export { commonDismissError as dismissErrorReducer, commonCloseAlert as closeAlertReducer }
